// src/shared/ui/components/header-tabs.tsx
// Underline tabs for the page header's `tabs` slot (design log 13 Oct, Rule "header tabs are
// underline tabs on the header's bottom line"): 13/500 labels 24 apart on the subline's bottom
// line, secondary ink (primary on hover), the active one primary ink with a 2px brand underline
// lying on the header's hairline. For tab sets whose labels carry more than text (a count); the
// DS SubTabs takes string labels only. Arrow keys, Home and End move between tabs.
//
// The tabs give way first when the header runs short of room (design log 13 Oct, Rule "the tabs
// go into More, the active tab always stays"; prototype ContextHeader `ViewTabs`): the ones that
// do not fit move into a "More" menu, which closes on a click outside it or Escape. Each tab's
// natural width is measured in a hidden row, again when the labels, the web font or the room
// change. With no layout (or nothing measured) every tab is shown.
// Looks: src/styles/showcase-bridge.css (`.page-header-tabs`, `.header-tabs-*`).
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { fitHeaderTabs } from './header-tabs-fit';
import { ChevronDown } from 'lucide-react';

export interface HeaderTab<T extends string> {
  id: T;
  label: ReactNode;
}

interface HeaderTabsProps<T extends string> {
  tabs: readonly HeaderTab<T>[];
  value: T;
  onChange: (id: T) => void;
  'aria-label': string;
}

export function HeaderTabs<T extends string>({ tabs, value, onChange, 'aria-label': ariaLabel }: HeaderTabsProps<T>) {
  const refs = useRef(new Map<T, HTMLButtonElement>());
  const boxRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState(0);
  const [widths, setWidths] = useState<number[]>([]);
  const [moreOpen, setMoreOpen] = useState(false);

  // The More menu closes on a click outside it or Escape.
  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!moreRef.current?.contains(e.target as Node)) setMoreOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  // Natural widths: measured on the hidden row, and again when the web font arrives.
  const [fontsReady, setFontsReady] = useState(0);
  useEffect(() => {
    let live = true;
    document.fonts?.ready?.then(() => {
      if (live) setFontsReady((n) => n + 1);
    });
    return () => {
      live = false;
    };
  }, []);
  const labelsKey = tabs.map((t) => t.id).join('|');
  useLayoutEffect(() => {
    const row = measureRef.current;
    if (!row) return;
    const measure = () =>
      setWidths((prev) => {
        const next = [...row.children].map((c) => (c as HTMLElement).offsetWidth);
        return prev.length === next.length && prev.every((w, i) => w === next[i]) ? prev : next;
      });
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(row);
    document.fonts?.addEventListener?.('loadingdone', measure);
    return () => {
      ro?.disconnect();
      document.fonts?.removeEventListener?.('loadingdone', measure);
    };
  }, [labelsKey, fontsReady, tabs]);

  // The room the row has, read again on every resize.
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const read = () => setRoom(box.clientWidth);
    read();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(read) : null;
    ro?.observe(box);
    window.addEventListener('resize', read);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', read);
    };
  }, []);

  const activeIndex = tabs.findIndex((t) => t.id === value);
  const { shown, more, allFit } = fitHeaderTabs(widths.length === tabs.length ? widths : [], room, activeIndex);
  const shownIndexes = shown.length > 0 || tabs.length === 0 ? shown : tabs.map((_, i) => i);
  const activeInMore = more.includes(activeIndex);

  const go = (id: T) => {
    onChange(id);
    refs.current.get(id)?.focus();
  };
  const onKeyDown = (e: KeyboardEvent, i: number) => {
    const last = shownIndexes.length - 1;
    const next =
      e.key === 'ArrowRight' ? (i + 1) % shownIndexes.length
      : e.key === 'ArrowLeft' ? (i - 1 + shownIndexes.length) % shownIndexes.length
      : e.key === 'Home' ? 0
      : e.key === 'End' ? last
      : null;
    if (next === null) return;
    e.preventDefault();
    go(tabs[shownIndexes[next]].id);
  };

  return (
    <div ref={boxRef} data-slot="header-tabs-box" className="header-tabs-box">
      {/* Measuring row: each tab's natural width, never seen. */}
      <div ref={measureRef} aria-hidden="true" className="header-tabs-measure">
        {tabs.map((t) => (
          <span key={t.id} className="header-tab">
            {t.label}
          </span>
        ))}
      </div>
      <div data-slot="header-tabs" data-all-fit={allFit ? 'true' : undefined} className="header-tabs">
        <div role="tablist" aria-label={ariaLabel} className="header-tabs-row">
          {shownIndexes.map((ti, i) => {
            const t = tabs[ti];
            const on = t.id === value;
            return (
              <button
                key={t.id}
                ref={(el) => {
                  if (el) refs.current.set(t.id, el);
                  else refs.current.delete(t.id);
                }}
                type="button"
                role="tab"
                aria-selected={on}
                tabIndex={on ? 0 : -1}
                className="header-tab"
                onClick={() => onChange(t.id)}
                onKeyDown={(e) => onKeyDown(e, i)}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {more.length > 0 && (
          <div ref={moreRef} className="header-tabs-more">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              data-slot="header-tabs-more"
              className="header-tab"
              data-active={activeInMore ? 'true' : undefined}
              onClick={() => setMoreOpen((v) => !v)}
            >
              More <ChevronDown className="size-4" aria-hidden="true" />
            </button>
            {moreOpen && (
              <div role="menu" aria-label="More" className="header-tabs-menu">
                {more.map((ti) => (
                  <button
                    key={tabs[ti].id}
                    type="button"
                    role="menuitem"
                    className="header-tabs-menu-item"
                    onClick={() => {
                      setMoreOpen(false);
                      onChange(tabs[ti].id);
                    }}
                  >
                    {tabs[ti].label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
